/**
 * ╔══════════════════════════════════════════════════════════════════════════╗
 * ║  GENERATED FILE — DO NOT EDIT                                            ║
 * ║                                                                          ║
 * ║  Source : functions/betMath.js                                           ║
 * ║  Regen  : npm run math:sync                                              ║
 * ║  Verify : npm run math:verify   (runs in the predeploy gate)             ║
 * ║                                                                          ║
 * ║  Edit the source, then re-run the sync. Hand edits here are overwritten  ║
 * ║  and will fail the gate.                                                 ║
 * ╚══════════════════════════════════════════════════════════════════════════╝
 */

/**
 * betMath.js — THE canonical bet math core.
 *
 * ════════════════════════════════════════════════════════════════════════════
 * THIS IS THE ONE FILE. Every odds conversion, devig, EV, R-factor, and Kelly
 * calculation in the platform must ultimately resolve to a function in here.
 *
 * Rules:
 *   1. PURE. No I/O, no Firestore, no `require` of anything. Deterministic.
 *   2. Every magic number is a NAMED, DOCUMENTED constant in LIMITS below.
 *   3. Numeric behavior is frozen by `functions/__tests__/betMath.test.js`
 *      golden vectors. Changing a formula must change a test on purpose.
 *   4. The frontend mirror `website/utils/betMath.js` is GENERATED from this
 *      file by `npm run math:sync`. Never edit the mirror by hand.
 *
 * Why a generated mirror instead of a shared import: Firebase deploys only the
 * `functions/` directory, and the Expo web app's Metro root is `website/`.
 * Neither can reach a common parent at runtime, so the file is authored once
 * here and mechanically copied. `npm run math:verify` fails the predeploy gate
 * if the two ever drift.
 * ════════════════════════════════════════════════════════════════════════════
 */

// ═══════════════════════════════════════════════════════════════════════════
// LIMITS — every clamp, cap, floor, and threshold in the platform's math.
// Previously these were ~30 inline magic numbers spread across 12 files.
// ═══════════════════════════════════════════════════════════════════════════

const LIMITS = {
  /** Fair probability clamp. A devigged prob can never leave this band. */
  PROB_MIN: 0.001,
  PROB_MAX: 0.999,

  /**
   * Additive devig is only trustworthy in the mid-range. Subtracting a flat
   * overround from a near-zero implied prob yields nonsense (e.g. -87% EV on a
   * +2000 longshot). Outside this band, additive is dropped from the min-set.
   */
  ADDITIVE_RELIABLE_MIN: 0.05,
  ADDITIVE_RELIABLE_MAX: 0.95,

  /**
   * Two-way total-implied trust ceiling. Above this means mispaired lines or a
   * triple-booked market, so we fall back to assumed juice. (There is no floor:
   * a real pair must already total >= 1.0 to be accepted at all.)
   */
  TOTAL_IMPLIED_MALFORMED_CEILING: 1.50,

  /**
   * Lower bound for per-book no-vig pair checks that, unlike
   * resolveTotalImplied, accept totals below 1.0 (futuresRefresh,
   * bestBetsCache). Below this the pair is a DFS/flex-pick shape, not a real
   * two-way market. Not used inside resolveTotalImplied/calculateAllDevig.
   */
  TOTAL_IMPLIED_DFS_FLOOR: 0.50,

  /** Legacy flat assumed juice when only one side of a market exists. */
  ASSUMED_VIG_DEFAULT: 1.10,

  /** Opposite-odds sentinel sanitize range (API sends -99900 for "no line"). */
  OPPOSITE_ODDS_MIN: -5000,
  OPPOSITE_ODDS_MAX: 5000,

  /** Power-devig binary search bounds and iteration count. */
  POWER_K_LO: 0.01,
  POWER_K_HI: 20,
  POWER_ITERATIONS: 64,

  /** Default trim fraction for robustMean when n >= 4. */
  ROBUST_MEAN_TRIM: 0.2,

  /**
   * SGP R-factor bounds. R = legsDecimal / avgBookSgpDecimal measures how much
   * correlation the market is pricing in. R < 1 means legs are positively
   * correlated (books price the parlay shorter than independence implies).
   *
   * There is deliberately NO lower bound. A former floor of 0.50 turned a true
   * R of 0.076 into 0.50, inflating fair probability ~6.5x and producing a fake
   * +578% edge. A low R can only lower EV, so it never needs protecting against.
   */
  R_FACTOR_MAX: 1.30,

  /**
   * Stored SGP EV caps (sgpOptimizer stamps, per-book re-pricing). With 2+
   * consensus books the R-factor is cross-checked and EV is capped at 25%;
   * with one book there is no consensus to validate the correlation, so the
   * cap tightens to 10%. Display caps (displayEvCap) apply on top.
   */
  SGP_EV_CAP_CONSENSUS: 25,
  SGP_EV_CAP_SINGLE_BOOK: 10,

  /**
   * A book a user adds to their SGP fair value counts only when its quote is
   * within this fraction of the default consensus's robust mean (about the
   * DK/FD/MGM spread on one SGP). Further out it is shown but not counted, so
   * a low-priced book cannot pull the consensus down into phantom +EV.
   */
  SGP_USER_BOOK_TRACKING_BAND: 0.12,

  /**
   * Floor on the STORED SGP EV stamps (ev_percent, ev_method*). Display only:
   * the unclipped values are stored alongside (ev_raw, ev_method*_raw) and all
   * re-pricing reads those, so a clipped stamp is never inverted.
   */
  SGP_EV_STAMP_FLOOR: -50,

  /**
   * SGP correlated fair-probability bounds. SGP_PROB_MAX caps the top;
   * SGP_PROB_MIN is only a stand-in for a degenerate non-positive result
   * (R <= 0), never a floor on a genuine small probability.
   */
  SGP_PROB_MIN: 0.001,
  SGP_PROB_MAX: 0.99,

  /** Kelly: maximum fraction of bankroll any single bet may be sized at. */
  KELLY_MAX_FRACTION: 0.50,

  /**
   * Display/sort EV caps applied by annotateCleanEv after contamination checks.
   * Props can legitimately show higher method EV than game lines; game lines above
   * ~35% are almost always data artifacts (alt-line mismatch, DFS outliers).
   */
  PROP_EV_DISPLAY_CAP: 35,
  LINE_EV_DISPLAY_CAP: 50,

  /**
   * Single-book price outlier (evCleanPerBook): a book whose implied prob sits
   * at least PRICE_OUTLIER_GAP below the median of the OTHER real books on the
   * same line (needs PRICE_OUTLIER_MIN_BOOKS of them) is a stale/bad quote. It
   * keeps its row but can't be the best book or set the item's EV. Example:
   * Fanatics +175 (36%) vs 16 books at −154…+110 (median 53.5%). Tunable.
   */
  PRICE_OUTLIER_GAP: 0.12,
  PRICE_OUTLIER_MIN_BOOKS: 4,
};

// ═══════════════════════════════════════════════════════════════════════════
// SECTION 1 — Odds conversions
//
// These replaced 27 separate copy-pasted implementations. All four are exact
// algebraic inverses of each other; `roundTripCheck` proves it.
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Is this a valid American price? American odds never sit strictly between
 * -100 and +100: +50 or -50 is a malformed quote (often a decimal or a
 * percentage in the wrong field), not a price. It used to convert to decimal
 * 1.5 and imply 67%, so one bad feed row could price a leg. ±100 is even money.
 */
function isValidAmerican(american) {
  return Number.isFinite(american) && Math.abs(american) >= 100;
}

/** American odds → implied probability (vig included). -110 → 0.5238 */
function americanToImplied(american) {
  if (!isValidAmerican(american)) return null;
  return american > 0
    ? 100 / (american + 100)
    : Math.abs(american) / (Math.abs(american) + 100);
}

/**
 * Guard against a finite input producing a non-finite result. Denormal inputs
 * (e.g. 5e-324) overflow to Infinity in these ratios, and an Infinity that
 * reaches the EV formula yields an Infinity edge. Found by property testing.
 */
function finiteOrNull(v) {
  return Number.isFinite(v) ? v : null;
}

/** American odds → decimal odds. -110 → 1.9091, +150 → 2.50 */
function americanToDecimal(american) {
  if (!isValidAmerican(american)) return null;
  return finiteOrNull(american > 0
    ? american / 100 + 1
    : 100 / Math.abs(american) + 1);
}

/** Decimal odds → implied probability. 2.50 → 0.40 */
function decimalToImplied(decimal) {
  if (!Number.isFinite(decimal) || decimal <= 1) return null;
  return 1 / decimal;
}

/** Implied probability → decimal odds. 0.40 → 2.50 */
function impliedToDecimal(prob) {
  if (!Number.isFinite(prob) || prob <= 0 || prob >= 1) return null;
  return 1 / prob;
}

/**
 * Implied probability → American odds. 0.40 → +150, 0.5238 → -110
 *
 * Note the strict `>` at the 0.5 boundary. Even money can be written either
 * -100 or +100 — they are the same price — but +100 is the industry convention
 * and is what Crazy Ninja Math returns, so exactly 50% maps to +100. Verified
 * against the CNM API in betMath.oracle.test.js.
 */
function impliedToAmerican(prob) {
  if (!Number.isFinite(prob) || prob <= 0 || prob >= 1) return null;
  return finiteOrNull(prob > 0.5
    ? -(prob / (1 - prob)) * 100
    : ((1 - prob) / prob) * 100);
}

/** Decimal odds → American odds. 1.9091 → -110 */
function decimalToAmerican(decimal) {
  const p = decimalToImplied(decimal);
  return p === null ? null : impliedToAmerican(p);
}

/** Profit multiple on a 1-unit stake — the `b` in the Kelly formula. */
function payoutRatio(american) {
  const d = americanToDecimal(american);
  return d === null ? null : d - 1;
}

// ═══════════════════════════════════════════════════════════════════════════
// SECTION 2 — Devig (removing the bookmaker's margin)
//
// A book prices both sides so implied probabilities sum to > 1. That excess is
// the vig. Devigging redistributes it to recover the book's true estimate.
// Three methods disagree about HOW the vig is distributed; we compute all
// three and take the most conservative result.
// ═══════════════════════════════════════════════════════════════════════════

function clampProb(p) {
  if (!Number.isFinite(p)) return null;
  return Math.max(LIMITS.PROB_MIN, Math.min(LIMITS.PROB_MAX, p));
}

/**
 * Clamp for a DEVIGGED probability. A positive result is kept as-is (only capped
 * at PROB_MAX): flooring a genuine longshot UP to PROB_MIN would push the fair
 * probability above the offered implied probability and manufacture +EV (e.g.
 * +150000 showed +50%). Only a non-positive result (additive overshoot) is
 * replaced, and never with more than the book's own implied probability.
 */
function clampDevigProb(p, impliedProb) {
  if (!Number.isFinite(p)) return null;
  if (p > 0) return Math.min(LIMITS.PROB_MAX, p);
  const floor = Number.isFinite(impliedProb) && impliedProb > 0
    ? Math.min(LIMITS.PROB_MIN, impliedProb)
    : LIMITS.PROB_MIN;
  return floor;
}

/**
 * ADDITIVE — subtract the overround equally from every outcome.
 * Assumes the book applies vig as a flat amount, independent of price.
 * Accurate mid-range, badly wrong on longshots (can go negative).
 *
 *   fair = p - (totalImplied - 1) / numSides
 */
function devigAdditive(impliedProb, totalImplied, numSides = 2) {
  if (!impliedProb || !totalImplied || totalImplied <= 0) return null;
  if (!numSides || numSides < 2) return null;
  return clampDevigProb(impliedProb - (totalImplied - 1) / numSides, impliedProb);
}

/**
 * MULTIPLICATIVE — scale every outcome down proportionally. The CNM standard.
 * Assumes vig is proportional to price. Slightly favors favorites.
 *
 *   fair = p / totalImplied
 */
function devigMultiplicative(impliedProb, totalImplied) {
  if (!impliedProb || !totalImplied || totalImplied <= 0) return null;
  return clampDevigProb(impliedProb / totalImplied, impliedProb);
}

/**
 * POWER — solve for exponent k such that the devigged probs sum to exactly 1.
 * Handles heavy favorites correctly, which is where additive breaks down.
 *
 *   find k where Σ pᵢ^(1/k) = 1, then fair = p^(1/k)
 *
 * Solved by binary search on k. Monotonic in k, so 64 iterations over
 * [0.01, 20] converges far past double precision.
 */
function devigPower(impliedProb, totalImplied) {
  if (!Number.isFinite(impliedProb) || !Number.isFinite(totalImplied)) return null;
  if (impliedProb <= 0 || impliedProb >= 1 || totalImplied <= 0) return null;
  const p2 = totalImplied - impliedProb;
  // Degenerate two-way shape — fall back to multiplicative.
  if (p2 <= 0 || p2 >= 1) return devigMultiplicative(impliedProb, totalImplied);

  let lo = LIMITS.POWER_K_LO;
  let hi = LIMITS.POWER_K_HI;
  for (let i = 0; i < LIMITS.POWER_ITERATIONS; i++) {
    const mid = (lo + hi) / 2;
    if (Math.pow(impliedProb, 1 / mid) + Math.pow(p2, 1 / mid) > 1) hi = mid;
    else lo = mid;
  }
  const fair = Math.pow(impliedProb, 2 / (lo + hi));
  if (!Number.isFinite(fair) || fair <= 0 || fair >= 1) {
    return devigMultiplicative(impliedProb, totalImplied);
  }
  // Same clamp as the other two methods, for parity. Found by property testing.
  return clampDevigProb(fair, impliedProb);
}

/**
 * POWER, N-way — same solve generalized to any number of outcomes (3-way
 * moneylines, categorical markets like "first goalscorer").
 *
 *   find k where Σ pᵢ^(1/k) = 1 over ALL outcomes
 */
function devigPowerNWay(targetImplied, allImplieds) {
  if (!targetImplied || !Array.isArray(allImplieds)) return null;
  const probs = allImplieds.filter((p) => Number.isFinite(p) && p > 0 && p < 1);
  if (probs.length < 2) return null;

  let lo = LIMITS.POWER_K_LO;
  let hi = LIMITS.POWER_K_HI;
  for (let i = 0; i < LIMITS.POWER_ITERATIONS; i++) {
    const mid = (lo + hi) / 2;
    const sum = probs.reduce((s, p) => s + Math.pow(p, 1 / mid), 0);
    if (sum > 1) hi = mid;
    else lo = mid;
  }
  return Math.pow(targetImplied, 2 / (lo + hi));
}

/**
 * Run all three devig methods at once.
 * @returns {{ additive, multiplicative, power }} fair probabilities
 */
function devigAll(impliedProb, totalImplied, numSides = 2) {
  return {
    additive: devigAdditive(impliedProb, totalImplied, numSides),
    multiplicative: devigMultiplicative(impliedProb, totalImplied),
    power: devigPower(impliedProb, totalImplied),
  };
}

/**
 * Is additive trustworthy at this implied probability?
 *
 * Also false when the vig is ASSUMED (no real opposite side, devig source
 * 'estimated'). Additive subtracts half the assumed overround from the price,
 * so a +1900 longshot (5% implied) under the 10% fallback devigs to ~0% and
 * reads as -98% EV; multiplicative/power stay sane. (Oct 2026, frozen-data audit.)
 */
function isAdditiveReliable(impliedProb, { estimatedVig = false } = {}) {
  if (estimatedVig) return false;
  return Number.isFinite(impliedProb)
    && impliedProb >= LIMITS.ADDITIVE_RELIABLE_MIN
    && impliedProb <= LIMITS.ADDITIVE_RELIABLE_MAX;
}

/**
 * Power devig under an ASSUMED vig (no real opposite side at any book).
 *
 * Power needs a real opposite price. With an assumed total T the "opposite" is
 * the made-up complement T - p, which for a longshot sits near 1 (e.g. +1900:
 * p = 0.05, T = 1.045 → opposite 0.995). Power then loads almost the whole
 * overround onto the longshot: fair 0.008 instead of 0.048, so a one-sided
 * +1900 read −84% EV (and about −97% as the complement approaches 1). That
 * number is an artifact of the guessed opposite, not information.
 *
 * Rule (Trev, Oct 17 2026): under an assumed vig, multiplicative is the model
 * (the assumed vig is applied proportionally: fair = p / T). Power stays in
 * the min-set only as a veto: its EV is floored at break-even (0%). So power
 * can still cut or cancel an edge, but it cannot push a price below 0%. This
 * keeps the project rule "no phantom EV" exactly:
 *   · old EV > 0  → new EV = min(mult, power) = old EV (never inflated);
 *   · old EV <= 0 → new EV = min(mult EV, 0) <= 0 (never created).
 * The floor applies only when a finite multiplicative EV is in the set, and
 * only when `estimatedVig` is true, so two-sided markets are untouched.
 *
 * @param {number|null} powerEv - power-method EV% at the offered price
 * @param {{ estimatedVig?: boolean, multiplicativeEv?: number|null }} [opts]
 * @returns {number|null} the power EV to put in the min-set
 */
function powerEvForMinSet(powerEv, { estimatedVig = false, multiplicativeEv = null } = {}) {
  if (!estimatedVig || !Number.isFinite(powerEv) || !Number.isFinite(multiplicativeEv)) return powerEv;
  return powerEv < 0 ? 0 : powerEv;
}

/**
 * Is the consensus in this devig_methods block built on an ASSUMED vig?
 * 'estimated' blocks are, unless oddsRefresh overrode the consensus fields
 * with a peer consensus that is two-sided by construction (stamped
 * `consensus_two_sided: true`); then its power is a real veto.
 */
function isAssumedVigConsensus(dm) {
  return !!dm && typeof dm === 'object' && dm.devig_source === 'estimated' && dm.consensus_two_sided !== true;
}

/**
 * Same rule as powerEvForMinSet, in fair-probability space, for callers that
 * take the min of fair probabilities and price it afterwards. Flooring the
 * power fair at the offered price's implied probability (1 / decimal) is the
 * same as flooring its EV at 0% at that price.
 *
 * @param {number|null} powerFair
 * @param {{ estimatedVig?: boolean, multiplicativeFair?: number|null, offeredImplied?: number|null }} [opts]
 * @returns {number|null}
 */
function powerFairForMinSet(powerFair, { estimatedVig = false, multiplicativeFair = null, offeredImplied = null } = {}) {
  if (!estimatedVig || !Number.isFinite(powerFair) || !Number.isFinite(multiplicativeFair)) return powerFair;
  if (!Number.isFinite(offeredImplied) || offeredImplied <= 0 || offeredImplied >= 1) return powerFair;
  return powerFair < offeredImplied ? offeredImplied : powerFair;
}

// ═══════════════════════════════════════════════════════════════════════════
// SECTION 3 — Expected value
// ═══════════════════════════════════════════════════════════════════════════

/**
 * THE EV formula. Everything else in this file exists to produce a trustworthy
 * `fairProb` to feed into it.
 *
 *   EV% = (fairProb × decimalOdds - 1) × 100
 *
 * Reads as: stake 1 unit, win `decimalOdds` back with probability `fairProb`.
 * Subtract the stake. Positive means the price is better than the true odds.
 */
function evPercent(fairProb, decimalOdds) {
  if (!fairProb || !decimalOdds || fairProb <= 0 || decimalOdds <= 0) return null;
  return (fairProb * decimalOdds - 1) * 100;
}

/** EV% directly from American odds. */
function evPercentFromAmerican(fairProb, american) {
  return evPercent(fairProb, americanToDecimal(american));
}

/**
 * Invert EV back to the fair probability that produced it. Used when the
 * backend only stamped an EV and the frontend needs to re-price it at a
 * different book.
 *
 *   fairProb = (EV%/100 + 1) / decimalOdds
 */
function fairProbFromEv(evPct, decimalOdds) {
  if (!Number.isFinite(evPct) || !Number.isFinite(decimalOdds) || decimalOdds <= 0) return null;
  return (evPct / 100 + 1) / decimalOdds;
}

/**
 * Conservative EV across the three devig methods.
 *
 * We show the MINIMUM, not the average. If the three methods disagree, the
 * disagreement is real uncertainty about the book's margin model, and we would
 * rather understate an edge than invent one.
 *
 * `includeAdditive` is an explicit policy flag because the two call sites
 * genuinely differ:
 *   · Display / sort / product-level SGP → include additive (extra
 *     conservatism ranks "too good to be true" parlays DOWN).
 *   · Per-leg SGP internals → exclude additive (it craters on longshot legs
 *     and those stamps are quality metrics, not a display axis).
 *
 * @returns {{ ev, method } | { ev: null, method: null }}
 */
function worstCaseEv(evs, opts = {}) {
  const includeAdditive = opts.includeAdditive !== false;
  const candidates = [];
  if (includeAdditive && Number.isFinite(evs.additive)) {
    candidates.push({ method: 'additive', ev: evs.additive });
  }
  if (Number.isFinite(evs.multiplicative)) {
    candidates.push({ method: 'multiplicative', ev: evs.multiplicative });
  }
  // Under an assumed vig, power is only a veto (powerEvForMinSet). A no-op
  // unless the caller passes estimatedVig: true.
  const powerEv = powerEvForMinSet(evs.power, {
    estimatedVig: opts.estimatedVig === true,
    multiplicativeEv: evs.multiplicative,
  });
  if (Number.isFinite(powerEv)) {
    candidates.push({ method: 'power', ev: powerEv });
  }
  if (candidates.length === 0) return { ev: null, method: null };
  const min = candidates.reduce((a, b) => (b.ev < a.ev ? b : a));
  return { ev: min.ev, method: min.method };
}

// ═══════════════════════════════════════════════════════════════════════════
// SECTION 4 — Consensus estimators
//
// One book's price is a noisy sample of the true probability. Aggregating
// across books is how we get a reference sharp enough to price against.
// ═══════════════════════════════════════════════════════════════════════════

/** Median of the finite values. Null when there are none. */
function median(values) {
  if (!Array.isArray(values)) return null;
  const finite = values.filter((v) => Number.isFinite(v));
  if (finite.length === 0) return null;
  const sorted = finite.slice().sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 1
    ? sorted[mid]
    : (sorted[mid - 1] + sorted[mid]) / 2;
}

/**
 * Outlier-resistant central estimate. Sample sizes here are tiny (2-40 books)
 * and one stale or mispriced book can move a plain mean enough to fabricate an
 * edge, so the estimator changes shape with n:
 *
 *   n = 1     → the value itself
 *   n = 2..3  → median (a trimmed mean would discard too much)
 *   n >= 4    → 20% trimmed mean (drops the tails, keeps the middle)
 *
 * Frontend and backend MUST agree here — this feeds the R-factor, so any
 * divergence shows up directly as pill-vs-sort EV disagreement.
 */
function robustMean(values, opts = {}) {
  const trim = Number.isFinite(opts.trim) ? opts.trim : LIMITS.ROBUST_MEAN_TRIM;
  if (!Array.isArray(values)) return null;
  const finite = values.filter((v) => Number.isFinite(v));
  const n = finite.length;
  if (n === 0) return null;
  if (n === 1) return finite[0];
  const sorted = finite.slice().sort((a, b) => a - b);
  if (n <= 3) {
    return n % 2 === 1
      ? sorted[Math.floor(n / 2)]
      : (sorted[n / 2 - 1] + sorted[n / 2]) / 2;
  }
  const cut = Math.min(Math.floor(n / 2) - 1, Math.ceil(trim * n));
  const keep = sorted.slice(cut, n - cut);
  if (keep.length === 0) return sorted[Math.floor(n / 2)];
  return keep.reduce((s, v) => s + v, 0) / keep.length;
}

// ═══════════════════════════════════════════════════════════════════════════
// SECTION 5 — SGP correlation (the R-factor)
//
// Legs of a same-game parlay are NOT independent. Multiplying leg fair probs
// assumes they are, so the independence product is wrong in both directions
// depending on which way the legs are correlated.
//
// Rather than model correlation ourselves, we read it out of the market: books
// quote the parlay directly, so the ratio between the independence-implied
// price and the actual quoted price IS the market's correlation estimate.
//
//   R = legsProductDecimal / robustMean(bookSgpDecimals)
//
// Worked example — two 2.0 legs imply a 4.00 parlay (25%):
//   · Book quotes 3.00 (33%) → it thinks the combo is MORE likely than
//     independence → POSITIVE correlation → R = 4.00/3.00 = 1.33
//   · Book quotes 8.00 (12.5%) → LESS likely than independence →
//     NEGATIVE correlation → R = 4.00/8.00 = 0.50
//
//   R ≈ 1  → market agrees legs are roughly independent
//   R > 1  → POSITIVE correlation; books price the parlay SHORTER
//   R < 1  → NEGATIVE correlation; books price the parlay LONGER
//
// Then: fairSgpProb = Π(legFairProbs) × R
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Clamp a raw R-factor: upper cap only, NEVER a floor.
 *
 * History: a floor of 0.50 was the single worst bug in the platform's math. A
 * real production case: "Dort Under 1.5 threes + Dort Over 9.5 points" are
 * strongly NEGATIVELY correlated (scoring more points usually means making more
 * threes), so books quoted the combo far longer than independence implied — a
 * true R of 0.076. Flooring that to 0.50 multiplied the fair probability by
 * 6.5x and manufactured a +578% edge that did not exist. Do not reintroduce it.
 *
 * @param {number} rawR
 */
function clampRFactor(rawR) {
  if (rawR == null || !Number.isFinite(rawR)) return 1;
  return rawR > LIMITS.R_FACTOR_MAX ? LIMITS.R_FACTOR_MAX : rawR;
}

/**
 * Cap displayed/sort EV for ghost edges (props vs game lines).
 * Only clips positive EV above the tier cap; negative EV passes through.
 */
function displayEvCap(ev, isPlayerProp) {
  if (!Number.isFinite(ev)) return null;
  const cap = isPlayerProp ? LIMITS.PROP_EV_DISPLAY_CAP : LIMITS.LINE_EV_DISPLAY_CAP;
  return ev > cap ? cap : ev;
}

/**
 * Whether an item should use the player-prop EV cap tier.
 */
function isPlayerPropItem(item) {
  if (!item || typeof item !== 'object') return false;
  return !!(item._isPlayerProp || item.is_player_prop || item.player_name);
}

/**
 * Compute the market-implied correlation factor.
 *
 * @param {number} legsProductDecimal - Π decimal(leg) — the independence price
 * @param {number[]} bookSgpDecimals  - actual quoted parlay decimals per book
 * @returns {{ r, rawR, avgBookDecimal, bookCount, clamped }}
 */
function computeRFactor(legsProductDecimal, bookSgpDecimals) {
  const decs = (bookSgpDecimals || []).filter((d) => Number.isFinite(d) && d > 1);
  const avg = robustMean(decs);
  if (!Number.isFinite(legsProductDecimal) || legsProductDecimal <= 1 || !avg || avg <= 1) {
    return { r: null, rawR: null, avgBookDecimal: avg ?? null, bookCount: decs.length, clamped: false };
  }
  const rawR = legsProductDecimal / avg;
  const r = clampRFactor(rawR);
  return {
    r,
    rawR,
    avgBookDecimal: avg,
    bookCount: decs.length,
    clamped: Math.abs(r - rawR) > 1e-9,
  };
}

/**
 * Correlation-adjusted fair probability for a parlay.
 *
 *   fairSgpProb = Π(legFairProbs) × R
 */
function sgpFairProb(legFairProbs, rFactor) {
  if (!Array.isArray(legFairProbs) || legFairProbs.length === 0) return null;
  let product = 1;
  for (const p of legFairProbs) {
    if (!Number.isFinite(p) || p <= 0) return null;
    product *= p;
  }
  const r = Number.isFinite(rFactor) ? rFactor : 1;
  const raw = product * r;
  // No lower floor on a positive result: flooring a ~1e-6 parlay up to 0.001
  // inflated its fair probability 1000x and hid how bad longshot parlays are.
  if (!(raw > 0)) return LIMITS.SGP_PROB_MIN;
  return Math.min(LIMITS.SGP_PROB_MAX, raw);
}

/**
 * Fair probability (fraction) → the stored display percent on SGP docs.
 * Values >= 1% keep the historical 0.1% rounding; a positive value below 1%
 * keeps 3 significant figures. 0.1% rounding there distorted EVs rebuilt from
 * this field (p=0.0015 stored as 0.2% read +33% high) and rounded tiny
 * values to 0, which used to be read back as "unknown → 50%".
 *
 * @param {number} p
 * @returns {number|null}
 */
function fairPercentForStore(p) {
  if (!Number.isFinite(p)) return null;
  if (p >= 0.01 || p <= 0) return Math.round(p * 1000) / 10;
  return Number((p * 100).toPrecision(3));
}

/**
 * Re-price a stamped SGP EV into a different R-factor / price frame.
 *
 * The backend stamps EV at the best book's price using its own R. To show a
 * per-book EV — or to heal a cache doc written with a floored R — invert to the
 * correlated fair prob, swap the R, and re-apply:
 *
 *   correlatedFP = (ev/100 + 1) / fromDecimal
 *   ev'          = (correlatedFP × (toR / fromR) × toDecimal - 1) × 100
 */
function rescaleSgpEv(ev, fromDecimal, toDecimal, fromR, toR) {
  if (!Number.isFinite(ev) || !Number.isFinite(fromDecimal) || fromDecimal <= 1) return null;
  if (!Number.isFinite(toDecimal) || toDecimal <= 1) return null;
  if (!Number.isFinite(fromR) || fromR <= 0 || !Number.isFinite(toR) || toR <= 0) return null;
  const correlatedFP = fairProbFromEv(ev, fromDecimal);
  if (correlatedFP === null) return null;
  return (correlatedFP * (toR / fromR) * toDecimal - 1) * 100;
}

// ═══════════════════════════════════════════════════════════════════════════
// SECTION 5b — SGP sportsbook tiers (which books form the R-factor consensus)
//
// The backend (sgpOptimizer.scoreSGP) builds R from MAINSTREAM books only, and
// the per-book re-pricing (evCleanPerBook, canonicalEv) must leave one book out
// of exactly that same set. The re-pricing used to take every book in
// bookOdds, offshore included, so one DraftKings price next to Betano/Bet365
// showed +20% while the stamp said -7.6%. This is the one definition;
// sgpOptimizer.getBookDisplayPriority calls it.
// ═══════════════════════════════════════════════════════════════════════════

/**
 * DFS / pick'em / sweepstakes display names (lowercase). Mirrors
 * functions/config/sportsbookData DFS_ONLY_BOOKS; sgpMath.golden.test.js fails
 * if that list gains a name this one lacks.
 */
const SGP_DFS_BOOK_NAMES = [
  'prizepicks', 'prizepicks 5 or 6 pick flex', 'prizepicks demons and goblins',
  'underdog fantasy', 'underdog fantasy 3 or 5 pick', 'underdog fantasy 6 pick',
  'underdog fantasy multipliers', 'underdog predictions', 'underdog sportsbook',
  'underdog fantasy 2 pick', 'sleeper', 'hotstreak', 'jock mkt', 'jock mkt 4 pick',
  'jock mkt 5 pick', 'mojo', 'vivid picks', 'vivid picks 4 pick', 'parlayplay',
  'parlayplay 4 pick', 'parlayplay 6 pick', 'thrivefantasy', 'monkey knife fight',
  'props builder', 'boom fantasy', 'boom fantasy 5 pick insured', 'sportsbattle',
  'sportsbattle 5 pick power', 'sportsquack 5 pick', 'superdraft 3 or 5 pick',
  'ownersbox', 'ownersbox 6 pick insured', 'no house advantage',
  'no house advantage 4 pick', 'no house advantage 5 pick insurance',
  'no house advantage 5 pick power', 'wanna parlay', 'parlaye', 'chalkboard',
  'betopenly instant dfs', 'dabble 3 or 5 pick', 'dabble 3 or 5 pick multipliers',
  'epick', 'epick 50 50', 'draftkings pick 2', 'draftkings pick 3',
  'draftkings predictions', 'draftkings pick 6', 'fliff superstars', 'splash sports',
  'fd predicts', 'betr picks',
];
const _SGP_DFS_NAME_SET = new Set(SGP_DFS_BOOK_NAMES);
const _SGP_DFS_SUBSTRINGS = [
  'prizepicks', 'underdog', 'parlayplay', 'dabble', 'sleeper', 'hotstreak',
  'betr picks', 'mojo fantasy', 'vivid picks', 'thrivefantasy', 'monkey knife',
  'superdraft', 'sportsbattle', 'boom fantasy', 'no house', 'wanna parlay',
  'parlaye', 'chalkboard', 'epick', 'pick 2)', 'pick 3)', 'fliff', 'rebet props',
  'sportzino',
];
const _SGP_TIER1_SUBSTRINGS = ['draftkings', 'fanduel', 'caesars', 'betmgm', 'fanatics'];
const _SGP_TIER2_SUBSTRINGS = [
  'betrivers', 'hard rock', 'betparx', 'pointsbet', 'espn bet', 'wynnbet', 'thescore',
  'barstool', 'si sportsbook', 'tipico', 'pinnacle', 'bovada', 'betonline', 'mybookie',
  'betway', 'superbook', 'circa', 'bally', 'bet105', 'golden nugget', 'resorts',
  'sporttrade', 'novig', 'prophet', 'blue book', 'william hill', 'unibet', 'betfair',
  'leovegas', 'betsson', 'bwin', 'midnite', 'nitrogen', 'thunderpick',
];

/** DFS / pick'em / sweepstakes book (never a real-money parlay price). */
function isSgpDfsBookName(displayName) {
  const b = String(displayName || '').toLowerCase();
  if (_SGP_DFS_NAME_SET.has(b)) return true;
  return _SGP_DFS_SUBSTRINGS.some((x) => b.includes(x));
}

/**
 * SGP book tier: 1 core US regulated, 2 secondary mainstream, 3 offshore /
 * regional (Betano, Bet365, 1xBet), 4 DFS. The DFS check runs first so
 * "DraftKings Pick 6" is never tier 1.
 */
function sgpBookTier(displayName) {
  const b = String(displayName || '').toLowerCase();
  if (isSgpDfsBookName(b)) return 4;
  if (b === 'borgata' || _SGP_TIER1_SUBSTRINGS.some((x) => b.includes(x))) return 1;
  if (_SGP_TIER2_SUBSTRINGS.some((x) => b.includes(x))) return 2;
  return 3;
}

/** Mainstream (tier 1-2): the books whose SGP prices build the R-factor. */
function isMainstreamSGPBook(displayName) {
  return sgpBookTier(displayName) <= 2;
}

/**
 * Books that are proven to resell another book's feed: Blue Book is FanDuel's,
 * ps3838 is Pinnacle's (config/sportsbookData). One feed counts once in the
 * consensus. Regional variants ("BetMGM (UK)", "Caesars (Colorado)") price
 * separately and stay separate books.
 */
const _SGP_MIRROR_PARENT = { 'blue book': 'fanduel', bluebook: 'fanduel', ps3838: 'pinnacle' };

/** The feed behind an SGP book: 'Blue Book' → 'fanduel'; any other name is itself. */
function sgpBookEntity(displayName) {
  const b = String(displayName || '').toLowerCase().trim();
  return _SGP_MIRROR_PARENT[b] || b;
}

/**
 * The consensus subset of priced SGP books, in scoreSGP's fallback order:
 * mainstream if any priced, else every non-DFS book, else everything. Every
 * book is still shown and can be best_book (Nov 3); this set only builds R and
 * the fair value (a low-priced offshore book in it makes phantom +EV). Mirror
 * books collapse to one entry per feed (the parent's own entry when present,
 * else the first), so a feed is never double-weighted.
 *
 * @param {Array<{book: string, decimal: number}>} priced
 * @returns {Array<{book: string, decimal: number}>}
 */
function sgpConsensusBooks(priced) {
  const valid = (Array.isArray(priced) ? priced : [])
    .filter((x) => x && Number.isFinite(x.decimal) && x.decimal > 1);
  const mainstream = valid.filter((x) => isMainstreamSGPBook(x.book));
  const nonDfs = valid.filter((x) => sgpBookTier(x.book) <= 3);
  const pool = mainstream.length > 0 ? mainstream : (nonDfs.length > 0 ? nonDfs : valid);
  const byEntity = new Map();
  for (const x of pool) {
    const e = sgpBookEntity(x.book);
    const cur = byEntity.get(e);
    if (!cur || (String(x.book).toLowerCase() === e && String(cur.book).toLowerCase() !== e)) byEntity.set(e, x);
  }
  return pool.filter((x) => byEntity.get(sgpBookEntity(x.book)) === x);
}

const _bookNameSet = (v) => new Set(
  (v instanceof Set ? Array.from(v) : (Array.isArray(v) ? v : []))
    .filter((b) => typeof b === 'string' && b.trim() !== '')
    .map((b) => String(b).toLowerCase().trim())
);

/**
 * The SGP consensus for one user's fair-value prefs. Only policy 'custom'
 * changes it; every other policy (and no prefs) is sgpConsensusBooks.
 *   · an excluded book leaves the set; excluding a parent feed (FanDuel) also
 *     drops its mirrors (Blue Book), excluding a mirror drops only the mirror;
 *   · an included book joins only when 2+ default books are left after the
 *     exclusions and its quote is within SGP_USER_BOOK_TRACKING_BAND of their
 *     robust mean, so added books can never be the whole market.
 *
 * @param {Array<{book: string, decimal: number}>} priced
 * @param {{policy?: string, excluded?: Set<string>|string[], included?: Set<string>|string[]}|null} opts
 * @returns {Array<{book: string, decimal: number}>}
 */
function sgpUserConsensusBooks(priced, opts) {
  const base = sgpConsensusBooks(priced);
  if (!opts || opts.policy !== 'custom') return base;
  const excluded = _bookNameSet(opts.excluded);
  const included = _bookNameSet(opts.included);
  const isExcluded = (book) => {
    const name = String(book).toLowerCase().trim();
    return excluded.has(name) || excluded.has(sgpBookEntity(book));
  };
  const out = base.filter((x) => !isExcluded(x.book));
  if (included.size === 0 || out.length < 2) return out;
  const mean = robustMean(out.map((x) => x.decimal));
  if (!(mean > 1)) return out;
  const have = new Set(out.map((x) => sgpBookEntity(x.book)));
  const kept = out.slice();
  for (const x of priced) {
    if (!x || !Number.isFinite(x.decimal) || x.decimal <= 1) continue;
    const e = sgpBookEntity(x.book);
    const name = String(x.book).toLowerCase().trim();
    if (!(included.has(name) || included.has(e)) || isExcluded(x.book) || have.has(e)) continue;
    if (Math.abs(x.decimal / mean - 1) > LIMITS.SGP_USER_BOOK_TRACKING_BAND) continue;
    kept.push(x);
    have.add(e);
  }
  return kept;
}

/** Stored SGP EV cap for a consensus size (25 with 2+ books, else 10). */
function sgpEvCap(consensusBookCount) {
  return Number.isFinite(consensusBookCount) && consensusBookCount >= 2
    ? LIMITS.SGP_EV_CAP_CONSENSUS
    : LIMITS.SGP_EV_CAP_SINGLE_BOOK;
}

/**
 * Inputs for sgpMethodRowsFromStamps / sgpPerBookEvFromStamps from a cached
 * SGP and one of its books. THE builder: evCleanPerBook (backend) and
 * canonicalEv (frontend) both call it, so they re-price identically.
 *
 *   · method stamps: the unclipped *_raw values when present and consistent
 *     with the clipped stamp (a read-time heal may have rescaled only the
 *     clipped stamp), else the clipped stamp;
 *   · R: r_factor_raw when consistent with r_factor, else r_factor;
 *   · leave-one-out set: the consensus books (sgpConsensusBooks) minus this
 *     book, so offshore books never enter it when a mainstream book priced;
 *   · consensusBookCount: the stored count, else the size of that set.
 *
 * With consensusOpts (a user's 'custom' fair-value books) that change the set,
 * the set is sgpUserConsensusBooks and the count is its size: the leave-one-out
 * R ratio uses the user set (the stored R is unchanged), and a set of 0-1 books
 * prices at R = 1 (rIndependent). Without them nothing changes.
 *
 * @param {Object} sgp - cached SGP (combined_price, ev_method*, r_factor, bookOdds …)
 * @param {string|null} bookName - bookOdds key being priced (null: match by price)
 * @param {number} bookPrice - that book's American price
 * @param {Object|null} [consensusOpts] - { policy, excluded, included }
 * @returns {Object|null}
 */
function sgpStampInput(sgp, bookName, bookPrice, consensusOpts = null) {
  if (!sgp || typeof sgp !== 'object') return null;
  const combinedPrice = sgp.combined_price != null ? sgp.combined_price : sgp.price;
  const priceOf = (bd) => ((bd && typeof bd === 'object')
    ? (bd.price != null ? bd.price : bd.american_price)
    : bd);
  const priced = [];
  const bo = sgp.bookOdds && typeof sgp.bookOdds === 'object' ? sgp.bookOdds : {};
  for (const [bk, bd] of Object.entries(bo)) {
    const d = americanToDecimal(priceOf(bd));
    if (d != null && d > 1) priced.push({ book: bk, decimal: d });
  }
  const base = sgpConsensusBooks(priced);
  const user = consensusOpts ? sgpUserConsensusBooks(priced, consensusOpts) : base;
  // The user's prefs count only when they change the set (else the stamp path).
  const userSet = user.length !== base.length || user.some((x) => !base.includes(x));
  const consensus = userSet ? user : base;
  let others;
  if (bookName != null && bookName !== '') {
    // Leave out this book's whole feed (a mirror priced the same thing).
    const ent = sgpBookEntity(bookName);
    others = consensus.filter((x) => sgpBookEntity(x.book) !== ent);
  } else {
    const selDec = americanToDecimal(bookPrice);
    const idx = consensus.findIndex((x) => selDec != null && Math.abs(x.decimal - selDec) < 1e-12);
    others = consensus.filter((_, i) => i !== idx);
  }
  const consensusBookCount = !userSet && Number.isFinite(sgp.consensus_book_count)
    ? sgp.consensus_book_count
    : consensus.length;
  const cap = sgpEvCap(consensusBookCount);
  // A raw stamp is used only when clipping it reproduces the stored stamp
  // (raw is stored to 0.01 and the stamp to 0.1, so they agree within 0.055).
  const stamp = (raw, clipped) => {
    if (!Number.isFinite(raw)) return clipped;
    if (!Number.isFinite(clipped)) return raw;
    const reclipped = Math.max(LIMITS.SGP_EV_STAMP_FLOOR, Math.min(cap, raw));
    return Math.abs(reclipped - clipped) <= 0.056 ? raw : clipped;
  };
  const rRaw = sgp.r_factor_raw;
  const rStored = Number.isFinite(sgp.r_factor) ? sgp.r_factor : 1;
  const rFactor = Number.isFinite(rRaw) && Math.abs(rRaw - rStored) <= 0.0006 ? rRaw : sgp.r_factor;
  return {
    combinedPrice,
    bookPrice,
    // A user set of 0-1 books prices like scoreSGP's single-book model (R = 1),
    // so an excluded book's quote cannot still shape the fair value via R.
    ...(userSet && consensus.length < 2 ? { rIndependent: true } : {}),
    evMethod1Additive: stamp(sgp.ev_method1_additive_raw, sgp.ev_method1_additive),
    evMethod2Multiplicative: stamp(sgp.ev_method2_multiplicative_raw, sgp.ev_method2_multiplicative),
    evMethod3Power: stamp(sgp.ev_method3_power_raw, sgp.ev_method3_power),
    rFactor,
    legsProductDecimal: sgp.legs_product_decimal,
    otherBookDecimals: others.map((x) => x.decimal),
    consensusBookCount,
    evCap: cap,
  };
}

// ═══════════════════════════════════════════════════════════════════════════
// SECTION 6 — Stake sizing (Kelly criterion)
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Kelly optimal stake as a fraction of bankroll.
 *
 *   f* = (b × p - q) / b     where b = profit multiple, q = 1 - p
 *
 * Full Kelly maximizes long-run growth but is brutally volatile, so callers
 * pass a `fraction` (½, ¼ Kelly). Result is floored at 0 (never bet a -EV
 * edge) and capped at KELLY_MAX_FRACTION as a bankroll-ruin guard.
 *
 * @param {number} fairProb - true win probability
 * @param {number} american - the price being offered
 * @param {number} [fraction=1] - Kelly multiplier (0.25 = quarter Kelly)
 */
function kellyFraction(fairProb, american, fraction = 1) {
  if (!Number.isFinite(fairProb) || fairProb <= 0 || fairProb >= 1) return null;
  const b = payoutRatio(american);
  if (b === null || b <= 0) return null;
  const q = 1 - fairProb;
  const full = (b * fairProb - q) / b;
  const scaled = full * (Number.isFinite(fraction) ? fraction : 1);
  return Math.max(0, Math.min(scaled, LIMITS.KELLY_MAX_FRACTION));
}

// ═══════════════════════════════════════════════════════════════════════════
// SECTION 7 — Full pipeline (the "plug in numbers, get everything" entry point)
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Resolve the total implied probability to devig against, and report where it
 * came from. Provenance matters: a same-book pair is ground truth, a cross-book
 * consensus is an estimate, and assumed juice is a guess.
 *
 * @returns {{ totalImplied, source, hasRealOpposite, oppositeAmerican }}
 *   source: 'same_book' | 'cross_book' | 'estimated'
 */
function resolveTotalImplied(impliedProb, oppositeAmerican, totalImpliedOverride, opts = {}) {
  const assumedVig = Number.isFinite(opts.assumedVig)
    ? opts.assumedVig
    : LIMITS.ASSUMED_VIG_DEFAULT;

  // Sentinel sanitize — the feed sends -99900 to mean "no opposing line".
  let opp = oppositeAmerican;
  if (opp != null && (opp < LIMITS.OPPOSITE_ODDS_MIN || opp > LIMITS.OPPOSITE_ODDS_MAX)) {
    opp = null;
  }

  let totalImplied = impliedProb;
  let hasRealOpposite = false;
  let source = 'estimated';

  if (opp !== null && opp !== undefined) {
    const oppImplied = americanToImplied(opp);
    if (oppImplied && oppImplied > 0) {
      const sameBookTotal = impliedProb + oppImplied;
      // A real two-way market always has some vig (total >= 1.0). Below that is
      // a data-quality problem, not a gift.
      if (sameBookTotal >= 1.0) {
        totalImplied = sameBookTotal;
        hasRealOpposite = true;
        source = 'same_book';
      }
    }
  }

  if (!hasRealOpposite
      && Number.isFinite(totalImpliedOverride)
      && totalImpliedOverride > 1) {
    totalImplied = totalImpliedOverride;
    hasRealOpposite = true;
    source = 'cross_book';
  }

  const isMalformed = hasRealOpposite && totalImplied > LIMITS.TOTAL_IMPLIED_MALFORMED_CEILING;

  if (!hasRealOpposite || isMalformed) {
    const estimatedOpposite = assumedVig - impliedProb;
    totalImplied = (estimatedOpposite > 0.01 && estimatedOpposite < 0.99)
      ? impliedProb + estimatedOpposite
      : assumedVig;
    // The supplied pair was rejected, so provenance must not claim it was used.
    hasRealOpposite = false;
    source = 'estimated';
    opp = null;
  }

  const oppImplied = clampProb(totalImplied - impliedProb);
  const derivedOppAmerican = oppImplied === null ? null : Math.round(impliedToAmerican(oppImplied));

  return {
    totalImplied,
    source,
    hasRealOpposite,
    oppositeAmerican: (opp !== null && opp !== undefined) ? opp : derivedOppAmerican,
  };
}

/**
 * ONE CALL, EVERY NUMBER. This is the function the admin calculator and every
 * caller should reach for: give it a price and whatever context you have, get
 * back the complete, fully-explained math.
 *
 * IMPORTANT — where positive EV actually comes from:
 * Devigging a single price can never produce +EV. Every method divides that
 * book's own implied probability down by its own overround, so the result is
 * always <= the offered price. A genuine edge only exists when a book's price is
 * compared against a fair probability sourced from SOMEWHERE ELSE — the
 * cross-book consensus. Pass `referenceFairProb` to model that: it prices the
 * offered odds against an external fair estimate instead of self-devigging.
 *
 * @param {Object} input
 * @param {number} input.american - the price being evaluated (required)
 * @param {number} [input.oppositeAmerican] - same-book other side
 * @param {number} [input.totalImpliedOverride] - cross-book consensus total
 * @param {number} [input.referenceFairProb] - external fair prob (consensus).
 *   When supplied, EV is computed against this instead of the self-devig, which
 *   is the only way a positive edge can appear.
 * @param {number} [input.assumedVig] - override the 1.10 fallback
 * @param {boolean} [input.includeAdditive=true] - additive in the min-set
 * @param {number} [input.kellyFraction=0.25]
 * @returns {Object} full breakdown — inputs, vig, three methods, EV, Kelly
 */
function calculate(input = {}) {
  const american = input.american;
  const implied = americanToImplied(american);
  const decimal = americanToDecimal(american);

  if (implied === null || decimal === null) {
    return { valid: false, error: 'Invalid American odds', input };
  }

  const vig = resolveTotalImplied(
    implied,
    input.oppositeAmerican ?? null,
    input.totalImpliedOverride ?? null,
    { assumedVig: input.assumedVig }
  );

  const selfDevig = devigAll(implied, vig.totalImplied, input.numSides ?? 2);

  // When an external reference fair prob is supplied, every method is priced
  // against it — the consensus is the truth and this book's price is the thing
  // being judged. Otherwise we self-devig (always <= 0% EV by construction).
  const ref = Number.isFinite(input.referenceFairProb) ? input.referenceFairProb : null;
  const usingReference = ref !== null && ref > 0 && ref < 1;
  const fair = usingReference
    ? { additive: ref, multiplicative: ref, power: ref }
    : selfDevig;

  const evs = {
    additive: evPercent(fair.additive, decimal),
    multiplicative: evPercent(fair.multiplicative, decimal),
    power: evPercent(fair.power, decimal),
  };

  // Self-devig EVs are always reported separately. Under a reference the three
  // entries in `evs` collapse to the same number (all methods priced against the
  // one consensus figure), so a per-method comparison has to read from here.
  const selfDevigEvs = usingReference
    ? {
      additive: evPercent(selfDevig.additive, decimal),
      multiplicative: evPercent(selfDevig.multiplicative, decimal),
      power: evPercent(selfDevig.power, decimal),
    }
    : evs;

  // Additive is dropped from the min-set when the implied prob is outside the
  // band where it behaves — but we still report its value for transparency.
  const additiveReliable = isAdditiveReliable(implied, { estimatedVig: vig.source === 'estimated' });
  // Under an assumed vig, power is a veto only (powerEvForMinSet). Priced
  // against a reference every method carries the same fair, so it is moot.
  const best = worstCaseEv(evs, {
    includeAdditive: (input.includeAdditive !== false) && additiveReliable,
    estimatedVig: vig.source === 'estimated' && !usingReference,
  });

  // A power EV floored at break-even prices at the offered implied prob.
  const chosenFair = !best.method ? null
    : (best.method === 'power' && best.ev !== evs.power) ? implied
      : fair[best.method];
  const kf = Number.isFinite(input.kellyFraction) ? input.kellyFraction : 0.25;

  return {
    valid: true,
    input: {
      american,
      oppositeAmerican: input.oppositeAmerican ?? null,
      totalImpliedOverride: input.totalImpliedOverride ?? null,
    },
    odds: {
      american,
      decimal,
      impliedProb: implied,
      impliedPercent: implied * 100,
      payoutRatio: decimal - 1,
    },
    vig: {
      totalImplied: vig.totalImplied,
      vigPercent: (vig.totalImplied - 1) * 100,
      source: vig.source,
      hasRealOpposite: vig.hasRealOpposite,
      oppositeAmerican: vig.oppositeAmerican,
    },
    fairProbs: fair,
    selfDevigProbs: selfDevig,
    evPercents: evs,
    selfDevigEvPercents: selfDevigEvs,
    referenceFairProb: usingReference ? ref : null,
    additiveReliable,
    fairProbSource: usingReference ? 'reference_consensus' : 'self_devig',
    best: {
      evPercent: best.ev,
      method: best.method,
      fairProb: chosenFair,
      fairAmerican: chosenFair === null ? null : Math.round(impliedToAmerican(chosenFair)),
    },
    kelly: {
      fraction: kf,
      full: chosenFair === null ? null : kellyFraction(chosenFair, american, 1),
      scaled: chosenFair === null ? null : kellyFraction(chosenFair, american, kf),
    },
  };
}

/**
 * Full SGP calculation — legs in, correlation-adjusted EV out.
 *
 * @param {Object} input
 * @param {Array} input.legs - [{ american, oppositeAmerican?, totalImpliedOverride? }]
 * @param {number[]} input.bookSgpDecimals - quoted parlay decimals across books
 * @param {number} input.offeredAmerican - the parlay price being evaluated
 */
function calculateSgp(input = {}) {
  const legs = Array.isArray(input.legs) ? input.legs : [];
  if (legs.length < 2) return { valid: false, error: 'SGP requires at least 2 legs' };

  const legResults = [];
  let legsProductDecimal = 1;
  const fairByMethod = { additive: 1, multiplicative: 1, power: 1 };

  for (const leg of legs) {
    const r = calculate({ ...leg, includeAdditive: input.includeAdditive });
    if (!r.valid) return { valid: false, error: `Invalid leg: ${leg.american}` };
    legResults.push(r);
    legsProductDecimal *= r.odds.decimal;
    fairByMethod.additive *= (r.fairProbs.additive ?? 0);
    fairByMethod.multiplicative *= (r.fairProbs.multiplicative ?? 0);
    fairByMethod.power *= (r.fairProbs.power ?? 0);
  }

  const rInfo = computeRFactor(legsProductDecimal, input.bookSgpDecimals);
  const r = Number.isFinite(rInfo.r) ? rInfo.r : 1;

  const offered = input.offeredAmerican ?? decimalToAmerican(legsProductDecimal);
  const offeredDecimal = americanToDecimal(offered);

  const correlatedFair = {
    additive: sgpFairProb([fairByMethod.additive], r),
    multiplicative: sgpFairProb([fairByMethod.multiplicative], r),
    power: sgpFairProb([fairByMethod.power], r),
  };

  const evs = {
    additive: evPercent(correlatedFair.additive, offeredDecimal),
    multiplicative: evPercent(correlatedFair.multiplicative, offeredDecimal),
    power: evPercent(correlatedFair.power, offeredDecimal),
  };

  const best = worstCaseEv(evs, { includeAdditive: input.includeAdditive !== false });

  return {
    valid: true,
    legs: legResults,
    independence: {
      legsProductDecimal,
      legsProductAmerican: decimalToAmerican(legsProductDecimal),
      fairProbProduct: fairByMethod,
    },
    correlation: rInfo,
    correlatedFairProbs: correlatedFair,
    offered: { american: offered, decimal: offeredDecimal },
    evPercents: evs,
    best: { evPercent: best.ev, method: best.method },
  };
}

/**
 * Per-book SGP EV from optimizer method stamps, with leave-one-out R correction.
 *
 * This is the single formula for the card pill, sort axis, and backend
 * `clean_ev_percent` stamping. Backend (evCleanPerBook) and frontend
 * (canonicalEv) must both call this — never reimplement inline.
 *
 * The method stamps (ev_method1/2/3) are computed at the SGP's best-book price
 * using R_backend built from ALL consensus books. When evaluating a specific
 * book's price, we recompute R_excl without that book and rescale:
 *
 *   ev_excl = (correlatedFP × (R_excl / R_backend) × selDec − 1) × 100
 *
 * Return value is min(additive, multiplicative, power) — matching the math
 * block's "✓ Used" row and the client's conservative preference.
 *
 * @param {Object} input
 * @param {number} input.combinedPrice - combined american at best book (stamp frame)
 * @param {number} input.bookPrice - this book's american price to evaluate
 * @param {number} [input.evMethod1Additive]
 * @param {number} [input.evMethod2Multiplicative]
 * @param {number} [input.evMethod3Power]
 * @param {number} [input.rFactor=1]
 * @param {number} [input.legsProductDecimal]
 * @param {number[]} [input.otherBookDecimals] - other CONSENSUS books' SGP decimals
 *   (LOO set; build it with sgpStampInput, never from every book in bookOdds)
 * @param {number} [input.consensusBookCount] - books behind R; < 2 skips LOO
 * @param {number} [input.evCap] - stored upper cap (sgpEvCap) applied after re-pricing
 * @param {boolean} [input.rIndependent] - price at R = 1 (a user consensus of 0-1 books)
 * @returns {number|null}
 */
function sgpMethodRowsFromStamps(input = {}) {
  const combinedPrice = input.combinedPrice;
  const bookPrice = input.bookPrice;
  if (!Number.isFinite(combinedPrice) || !Number.isFinite(bookPrice)) return null;

  const bestDec = americanToDecimal(combinedPrice);
  const selDec = americanToDecimal(bookPrice);
  if (bestDec == null || selDec == null || bestDec <= 1 || selDec <= 1) return null;

  const rBackend = Number.isFinite(input.rFactor) && input.rFactor > 0 ? input.rFactor : 1;
  const combinedDec = Number.isFinite(input.legsProductDecimal) ? input.legsProductDecimal : null;

  let rExclRatio = 1;
  const others = Array.isArray(input.otherBookDecimals)
    ? input.otherBookDecimals.filter((d) => Number.isFinite(d) && d > 1)
    : [];
  // R came from one book (or none): the stamp used R = 1, there is no
  // consensus to leave a book out of, and an R built from the remaining
  // quotes would be a different model, not a correction.
  const looAllowed = !(Number.isFinite(input.consensusBookCount) && input.consensusBookCount < 2);

  if (input.rIndependent === true) {
    rExclRatio = 1 / rBackend;
  } else if (looAllowed && combinedDec != null && combinedDec > 1 && rBackend > 0 && others.length >= 1) {
    const avgExcl = robustMean(others);
    if (avgExcl != null && avgExcl > 1) {
      const rExcl = clampRFactor(combinedDec / avgExcl);
      rExclRatio = rExcl / rBackend;
    }
  }

  // Optional stored cap (sgpEvCap): upper bound only, applied after re-pricing.
  const evCap = Number.isFinite(input.evCap) ? input.evCap : Infinity;
  const mkRow = (stampedEv) => {
    if (!Number.isFinite(stampedEv)) return null;
    const fairProb = (stampedEv / 100 + 1) / bestDec;
    const ev = Math.min(evCap, (fairProb * rExclRatio * selDec - 1) * 100);
    return Number.isFinite(ev) ? { fairProb, ev: parseFloat(ev.toFixed(2)) } : null;
  };

  const additive = mkRow(input.evMethod1Additive);
  const multiplicative = mkRow(input.evMethod2Multiplicative);
  const power = mkRow(input.evMethod3Power);
  if (!additive && !multiplicative && !power) return null;

  return {
    additive,
    multiplicative,
    power,
    looApplied: looAllowed && others.length >= 1 && combinedDec != null && combinedDec > 1 && rExclRatio !== 1,
    rExclRatio,
  };
}

/**
 * Canonical cached-SGP per-book EV: min(add, mult, pow) with LOO R-ratio when peer books exist.
 * @returns {number|null}
 */
function sgpPerBookEvFromStamps(input = {}) {
  const rows = sgpMethodRowsFromStamps(input);
  if (!rows) return null;
  const evs = [rows.additive?.ev, rows.multiplicative?.ev, rows.power?.ev].filter((v) => Number.isFinite(v));
  if (!evs.length) return null;
  return Math.min(...evs);
}

// ═══════════════════════════════════════════════════════════════════════════
// SECTION 8 — Self-verification
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Prove the conversions are mutually consistent and the devig methods sum to 1.
 * Runs in-process so the admin page can display a live green/red integrity
 * check rather than asking anyone to trust the code.
 *
 * @returns {{ passed: boolean, checks: Array<{name, passed, detail}> }}
 */
function selfCheck() {
  const checks = [];
  const near = (a, b, eps = 1e-9) => Number.isFinite(a) && Number.isFinite(b) && Math.abs(a - b) < eps;

  const prices = [-1000, -250, -110, -101, 101, 110, 250, 1000];

  checks.push({
    name: 'american → decimal → american round-trips',
    passed: prices.every((p) => near(decimalToAmerican(americanToDecimal(p)), p, 1e-6)),
    detail: 'decimalToAmerican(americanToDecimal(p)) === p',
  });

  checks.push({
    name: 'american → implied → american round-trips',
    passed: prices.every((p) => near(impliedToAmerican(americanToImplied(p)), p, 1e-6)),
    detail: 'impliedToAmerican(americanToImplied(p)) === p',
  });

  checks.push({
    name: 'implied and decimal agree (p = 1/d)',
    passed: prices.every((p) => near(americanToImplied(p), decimalToImplied(americanToDecimal(p)), 1e-12)),
    detail: 'americanToImplied(p) === 1 / americanToDecimal(p)',
  });

  checks.push({
    name: 'payoutRatio equals decimal - 1',
    passed: prices.every((p) => near(payoutRatio(p), americanToDecimal(p) - 1)),
    detail: 'Kelly b === decimal - 1 for both odds signs',
  });

  checks.push({
    name: 'even money renders as +100 (CNM convention)',
    passed: near(impliedToAmerican(0.5), 100) && near(americanToDecimal(100), 2.0),
    detail: 'impliedToAmerican(0.5) === +100, not -100',
  });

  // A fair two-way market devigs back to probabilities summing to 1.
  const pA = americanToImplied(-110);
  const pB = americanToImplied(-110);
  const ti = pA + pB;
  const multSum = devigMultiplicative(pA, ti) + devigMultiplicative(pB, ti);
  const addSum = devigAdditive(pA, ti) + devigAdditive(pB, ti);
  const powSum = devigPower(pA, ti) + devigPower(pB, ti);

  checks.push({
    name: 'multiplicative devig sums to 1.0',
    passed: near(multSum, 1, 1e-9),
    detail: `sum = ${multSum}`,
  });
  checks.push({
    name: 'additive devig sums to 1.0',
    passed: near(addSum, 1, 1e-9),
    detail: `sum = ${addSum}`,
  });
  checks.push({
    name: 'power devig sums to 1.0',
    passed: near(powSum, 1, 1e-6),
    detail: `sum = ${powSum}`,
  });

  // A perfectly fair coin-flip price at true 50% must be exactly 0% EV.
  checks.push({
    name: 'fair price yields exactly 0% EV',
    passed: near(evPercent(0.5, 2.0), 0, 1e-12),
    detail: 'evPercent(0.5, 2.0) === 0',
  });

  // EV inversion must be lossless.
  checks.push({
    name: 'EV inverts back to its fair probability',
    passed: near(fairProbFromEv(evPercent(0.55, 2.0), 2.0), 0.55, 1e-12),
    detail: 'fairProbFromEv(evPercent(p, d), d) === p',
  });

  // The bug this whole audit exists to catch.
  checks.push({
    name: 'R-factor does NOT floor a genuine low R',
    passed: near(clampRFactor(0.076), 0.076),
    detail: 'clampRFactor(0.076) === 0.076 (a former 0.50 floor faked +578% EV)',
  });

  // robustMean shape transitions.
  checks.push({
    name: 'robustMean uses median at n<=3',
    passed: near(robustMean([1, 2, 100]), 2),
    detail: 'robustMean([1,2,100]) === 2 (median, not mean)',
  });
  checks.push({
    name: 'robustMean trims tails at n>=4',
    passed: near(robustMean([1, 2, 3, 1000]), 2.5),
    detail: 'robustMean([1,2,3,1000]) === 2.5 (outlier trimmed)',
  });

  return { passed: checks.every((c) => c.passed), checks };
}

export {
  LIMITS,
  // conversions
  isValidAmerican,
  americanToImplied,
  americanToDecimal,
  decimalToImplied,
  impliedToDecimal,
  impliedToAmerican,
  decimalToAmerican,
  payoutRatio,
  // devig
  clampProb,
  devigAdditive,
  devigMultiplicative,
  devigPower,
  devigPowerNWay,
  devigAll,
  isAdditiveReliable,
  powerEvForMinSet,
  powerFairForMinSet,
  isAssumedVigConsensus,
  // ev
  evPercent,
  evPercentFromAmerican,
  fairProbFromEv,
  worstCaseEv,
  // consensus
  median,
  robustMean,
  // sgp
  clampRFactor,
  computeRFactor,
  displayEvCap,
  isPlayerPropItem,
  sgpFairProb,
  fairPercentForStore,
  rescaleSgpEv,
  SGP_DFS_BOOK_NAMES,
  isSgpDfsBookName,
  sgpBookTier,
  isMainstreamSGPBook,
  sgpBookEntity,
  sgpConsensusBooks,
  sgpUserConsensusBooks,
  sgpEvCap,
  sgpStampInput,
  // staking
  kellyFraction,
  // pipeline
  resolveTotalImplied,
  calculate,
  calculateSgp,
  sgpPerBookEvFromStamps,
  sgpMethodRowsFromStamps,
  // verification
  selfCheck,
};
